import { Button, DropDownList, IconDots, Loader, Table } from '@adtech/ui';
import snackbarTypes from '@constants/snackbarTypes';
import snackbarsConstants from '@constants/snackbars';
import useProjects from '@hooks/useProjects';
import useSnackbar from '@hooks/useSnackbar';
import { useAppDispatch, useAppSelector } from '@hooks/useStore';
import { projectsActions } from '@redux/slices/projects';
import { getProject } from '@redux/slices/projects/api';
import { IAccessRequestByProject } from '@typings/projects';
import React, { FC, useState } from 'react';
import s from './AccessUsers.pcss';

interface IProps {
    accessRequestsListByProject: IAccessRequestByProject[];
}

enum ERequestsActions {
    edit = 'edit',
    view = 'view',
    reject = 'reject',
}

const AccessRequestsTable: FC<IProps> = ({ accessRequestsListByProject }) => {
    const dispatch = useAppDispatch();
    const accessRequestsListLoading = useAppSelector((state) => state.userSlice.accessRequestsListByProjectLoading);
    const userId = useAppSelector((state) => state.userSlice.id);
    const currentProject = useAppSelector((state) => state.projectsSlice.current);
    const [isLoading, setIsLoading] = useState(false);

    const { setSnackbar } = useSnackbar();
    const {
        api: { getAccessRequestsListByProject },
    } = useProjects();

    const actionsList = [
        {
            title: 'Редактирование',
            value: ERequestsActions.edit,
        },
        {
            title: 'Только просмотр',
            value: ERequestsActions.view,
        },
        {
            title: 'Отклонить',
            value: ERequestsActions.reject,
        },
    ];

    const renderContextMenuAction = (action: { title: string; value: ERequestsActions }, accessRequestId: number) => ({
        dropDownItemProps: {
            text: action.title,
            onClick: () => {
                setIsLoading(true);
                const accessParam = {
                    access_level: action.value === ERequestsActions.edit ? 0 : 1,
                    can_edit_project: action.value === ERequestsActions.edit,
                };

                dispatch(
                    projectsActions.changeAccessRequestStatus({
                        projectId: currentProject.id,
                        data: {
                            access_request_id: accessRequestId,
                            action_request: action.value === ERequestsActions.reject ? 'decline' : 'access',
                            ...(action.value !== ERequestsActions.reject ? accessParam : {}),
                        },
                    }),
                )
                    .then(({ payload }) => {
                        if (payload?.responseStatus === 200) {
                            getAccessRequestsListByProject(currentProject.id);
                            dispatch(getProject({ projectId: currentProject.id, userId }));
                            setSnackbar(
                                action.value === ERequestsActions.reject
                                    ? snackbarsConstants.ACCESS_DECLINED
                                    : snackbarsConstants.ACCESS_GRANTED,
                            );
                        } else {
                            setSnackbar('Произошла ошибка при запросе на сервер', snackbarTypes.ERROR);
                        }
                        setIsLoading(false);
                    })
                    .finally(() => setIsLoading(false));
            },
        },
    });

    const renderIcon = (accessRequestId: number) => (
        <div className={s.accessRequestsBtnWrap}>
            <DropDownList
                menuItems={actionsList.map((action) => renderContextMenuAction(action, accessRequestId))}
                customButton={
                    <Button
                        className={s.accessRequestsBtn}
                        size="small"
                        round
                        prefixIcon={<IconDots />}
                        type="dashed"
                    />
                }
            />
        </div>
    );

    const tableColumns = [
        {
            dataIndex: 'email',
        },
        {
            dataIndex: 'icon',
            render: renderIcon,
        },
    ];

    const prepareDataForRender = (accessRequest: IAccessRequestByProject) => {
        const { email, accessRequestId } = accessRequest;

        return {
            key: email,
            email,
            icon: accessRequestId,
        };
    };

    return (
        <Loader loading={accessRequestsListLoading || isLoading}>
            <Table
                className={s.usersTable}
                columns={tableColumns}
                dataSource={accessRequestsListByProject?.map((request: IAccessRequestByProject) =>
                    prepareDataForRender(request),
                )}
                pagination={false}
                showHeader={false}
            />
        </Loader>
    );
};

export default AccessRequestsTable;
